import { useCallback, useMemo, useRef } from "react";
import { type InjectedFormProps, reduxForm } from "redux-form";
import { CalloutIntent, DialogComponent } from "widgets";

import { simpleDropdownFieldValidation } from "../../app-utils/form.js";
import { type Attribute } from "../../graphql.js";
import { getAttributeLabel } from "../../redux/modules/sample-admin.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import style from "./SelectAttributeForm.module.css";

interface SelectAttributeFormOwnProps {
  availableAttributes: Attribute[];
  parentContext?: boolean;
  onAddAttributes: (attributes: Attribute[]) => void;
  onClose: () => void;
  isLoading?: boolean;
}

interface SelectAttributeFormFormValues {
  [FIELD_ATTRIBUTE]: Attribute[];
}

interface SelectAttributeFormProps
  extends SelectAttributeFormOwnProps,
    InjectedFormProps<SelectAttributeFormFormValues, SelectAttributeFormOwnProps> {}

const FORM_NAME = "SelectAttributeForm";
const FIELD_ATTRIBUTE = "ATTRIBUTE_LIST";

const formatOptionLabel = (attribute: Attribute) => <span>{getAttributeLabel(attribute)}</span>;

const SelectAttributeFormImpl = (props: SelectAttributeFormProps) => {
  const { onAddAttributes, onClose, handleSubmit } = props;

  const dialogRef = useRef<HTMLDivElement>(null);

  const handleSubmitData = useCallback(
    (values: SelectAttributeFormFormValues) => {
      const list = values[FIELD_ATTRIBUTE];
      if (list.length > 0) {
        onAddAttributes(list);
      }

      onClose();
      return Promise.resolve(true);
    },
    [onAddAttributes, onClose],
  );

  const availableAttributes = useMemo(
    () =>
      props.availableAttributes.sort((attr1, attr2) => {
        const label1 = getAttributeLabel(attr1);
        const label2 = getAttributeLabel(attr2);
        return label1.localeCompare(label2, "en", {
          sensitivity: "base",
          // ignorePunctuation: true,
        });
      }),
    [props.availableAttributes],
  );

  const warningMessage = useMemo(
    () =>
      props.parentContext && availableAttributes.length === 0
        ? "You probably did not create any custom parent attributes for this parent type yet. Therefore there is nothing to select here."
        : undefined,
    [availableAttributes.length, props.parentContext],
  );

  return (
    <DialogComponent
      mainButton={{
        label: "Add",
        onPress: handleSubmit(handleSubmitData),
      }}
      closeFn={onClose}
      errorCallout={
        warningMessage && {
          message: warningMessage,
          intent: CalloutIntent.Warning,
        }
      }
      ref={dialogRef}
      contentClassName={style.content}
    >
      <div style={{ minWidth: 700, maxWidth: 700 }}>
        <ReactSelectReduxFormField
          name={FIELD_ATTRIBUTE}
          isMulti
          closeMenuOnSelect={false}
          isClearable={false}
          isSearchable={true}
          getOptionValue={(option: Attribute) => String(option.id)}
          getOptionLabel={(option: Attribute) => option.attributeName}
          label={"Select"}
          tooltip={"Please select one or multiple attribute(s)."}
          placeholder={"Select attributes..."}
          filterOption={(option, filter) => getAttributeLabel(option.data).toLowerCase().includes(filter.toLowerCase())}
          component={ReactSelectField}
          options={availableAttributes}
          validate={simpleDropdownFieldValidation}
          formatOptionLabel={formatOptionLabel}
          isLoading={props.isLoading}
          menuPortalTarget={null}
        />
      </div>
    </DialogComponent>
  );
};

const SelectAttributeForm = reduxForm<SelectAttributeFormFormValues, SelectAttributeFormOwnProps>({
  form: FORM_NAME,
})(SelectAttributeFormImpl);
export { SelectAttributeForm };
